// src/components/AnalyticsDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';
import GeoAnalytics from './GeoAnalytics';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

function StatCard({ label, value, hint }) {
  return (
    <div className="card" style={{minWidth:180}}>
      <div style={{opacity:.7}}>{label}</div>
      <div style={{fontSize:'1.8rem', fontWeight:700}}>{value ?? 0}</div>
      {hint ? <div style={{opacity:.6, fontSize:12, marginTop:4}}>{hint}</div> : null}
    </div>
  );
}

const fmtDate = (d) => new Date(d).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit' });
const number  = (n) => (n ?? 0).toLocaleString('de-DE');

// Reusable: Tabelle mit "Top 5" + Auf/Zu Klappen
function TopTable({
  title,
  rows,
  columns,         // [{key:'path', label:'Seite', align:'left', render?}, ...]
  topN = 5,
  onRowClick,      // (row)=>void  (optional)
  emptyText = 'Keine Daten'
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return expanded ? rows : rows.slice(0, topN);
  }, [rows, expanded, topN]);

  const total = rows?.length ?? 0;

  return (
    <div className="card">
      <h3>{title}</h3>
      <table style={{width:'100%', borderCollapse:'collapse'}}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{textAlign: c.align==='right' ? 'right' : 'left'}}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(shown.length > 0) ? shown.map((row, idx) => (
            <tr key={idx}>
              {columns.map((c) => {
                const content = c.render ? c.render(row) : row[c.key];
                return (
                  <td
                    key={c.key}
                    style={{
                      padding: '.4rem .2rem',
                      textAlign: c.align==='right' ? 'right' : 'left',
                      fontVariantNumeric: c.align==='right' ? 'tabular-nums' : undefined,
                      cursor: onRowClick && c.key === columns[0].key ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (onRowClick && c.key === columns[0].key) onRowClick(row);
                    }}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          )) : (
            <tr><td colSpan={columns.length} style={{opacity:.7}}>{emptyText}</td></tr>
          )}
        </tbody>
      </table>

      {(total > topN) && (
        <div style={{marginTop: 10}}>
          <button
            className="secondary"
            onClick={() => setExpanded(v => !v)}
            aria-expanded={expanded}
          >
            {expanded ? 'Weniger anzeigen' : 'Alle anzeigen'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [path, setPath] = useState('');     // leer = alle Seiten
  const [code, setCode] = useState('');     // optional: QR-Slug-Filter
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setErr('');
      const qs = new URLSearchParams();
      if (path) qs.set('path', path);
      if (code) qs.set('code', code);

      const res  = await apiFetch(`stats_api.php${qs.toString() ? `?${qs}` : ''}`);
      const text = await res.text();
      let js;
      try { js = JSON.parse(text); }
      catch { setErr('Antwort ist kein gültiges JSON.'); setData(null); return; }
      if (js.error) setErr(js.message || 'Fehler vom Server');
      setData(js);
    } catch {
      setErr('Netzwerk- oder CORS-Fehler.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // NUR Pageviews-Serie für den Chart
  const pvSeries = useMemo(() => (data?.pv_series || []).map(s => ({
    date: s.date,
    pv:   s.count ?? 0,
  })), [data]);
  const hasSeriesData = pvSeries.some(s => (s.pv || 0) > 0);

  // Geräte-Donut-Daten
  const deviceRows = useMemo(() => {
    const d = data?.devices || {};
    return Object.entries(d).map(([k,v]) => ({ name: k, value: v }));
  }, [data]);
  const deviceTotal = deviceRows.reduce((s,r)=>s+(r.value||0),0);
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9457EB', '#E85D75'];

  const exportCSV = () => {
    const rows = [];
    rows.push(['Bereich','Kennzahl','Wert']);
    rows.push(['Pageviews','Heute', data?.totals?.today ?? 0]);
    rows.push(['Pageviews','Woche', data?.totals?.week ?? 0]);
    rows.push(['Pageviews','Monat', data?.totals?.month ?? 0]);
    rows.push(['Pageviews','Gesamt', data?.totals?.all ?? 0]);
    rows.push(['QR-Scans','Heute', data?.qr_totals?.today ?? 0]);
    rows.push(['QR-Scans','Woche', data?.qr_totals?.week ?? 0]);
    rows.push(['QR-Scans','Monat', data?.qr_totals?.month ?? 0]);
    rows.push(['QR-Scans','Gesamt', data?.qr_totals?.all ?? 0]);
    rows.push([]);
    rows.push(['Datum','Pageviews']);
    pvSeries.forEach(s => rows.push([s.date, s.pv]));
    if (!data?.path && Array.isArray(data?.top_pages)) {
      rows.push([]);
      rows.push(['Top-Seiten (alle)']);
      rows.push(['Seite','Views']);
      data.top_pages.forEach(r => rows.push([r.path, r.views]));
    }
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"','""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `analytics_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <h2>Analytics</h2>

      {/* Filterleiste */}
      <div style={{display:'flex', gap:8, alignItems:'center', margin:'0.5rem 0 1rem', flexWrap:'wrap'}}>
        <input
          value={path}
          onChange={e=>setPath(e.target.value)}
          placeholder="Alle Seiten"
          style={{padding:'.5rem', minWidth:220}}
        />
        <input
          value={code}
          onChange={e=>setCode(e.target.value)}
          placeholder="QR-Slug (optional)"
          style={{padding:'.5rem', minWidth:180}}
        />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? 'Laden…' : 'Aktualisieren'}
        </button>
        {(path || code) && (
          <button className="secondary" onClick={()=>{ setPath(''); setCode(''); setTimeout(load,0); }}>
            Alle zurücksetzen
          </button>
        )}
        <button className="secondary" onClick={exportCSV} disabled={!data}>CSV exportieren</button>
      </div>

      {err && (
        <div className="card" style={{borderColor:'crimson', color:'crimson', marginBottom:'1rem'}}>
          {err}
        </div>
      )}

      {!data ? null : (
        <>
          {/* KPIs */}
          <div className="cards" style={{display:'flex', gap:'1rem', flexWrap:'wrap', marginBottom:'1rem'}}>
            <StatCard label="Aufrufe Heute"  value={number(data.totals?.today)} />
            <StatCard label="Aufrufe Woche"  value={number(data.totals?.week)} />
            <StatCard label="Aufrufe Monat"  value={number(data.totals?.month)} />
            <StatCard label="Aufrufe Gesamt" value={number(data.totals?.all)} />
            <StatCard label="QR Heute"  value={number(data.qr_totals?.today)} />
            <StatCard label="QR Woche"  value={number(data.qr_totals?.week)} />
            <StatCard label="QR Monat"  value={number(data.qr_totals?.month)} />
            <StatCard label="QR Gesamt" value={number(data.qr_totals?.all)} />
          </div>

          {/* Charts */}
          <div className="grid" style={{display:'grid', gap:'1rem', gridTemplateColumns:'2fr 1fr'}}>
            <div className="card" style={{height:360}}>
              <h3 style={{marginBottom:8}}>Entwicklung (letzte 30 Tage)</h3>
              {!hasSeriesData ? (
                <div style={{opacity:.7, padding:'2rem'}}>Noch keine Daten für diesen Zeitraum/Filter.</div>
              ) : (
                <ResponsiveContainer width="100%" height="85%">
                  <LineChart data={pvSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={20} />
                    <YAxis allowDecimals={false} domain={[0, 'dataMax+1']} />
                    <Tooltip labelFormatter={fmtDate} formatter={(v)=>[number(v), 'Pageviews']} />
                    <Line type="monotone" dataKey="pv" stroke="#0088FE" strokeWidth={2} dot={false} name="Pageviews" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="card" style={{height:360}}>
              <h3 style={{marginBottom:8}}>Geräte (Pageviews)</h3>
              {deviceTotal === 0 ? (
                <div style={{opacity:.7}}>Keine Daten</div>
              ) : (
                <ResponsiveContainer width="100%" height="85%">
                  <PieChart>
                    <Pie data={deviceRows} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100}>
                      {deviceRows.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v)=>number(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Top-Listen */}
          <div className="grid" style={{display:'grid', gap:'1rem', gridTemplateColumns:'1fr 1fr', marginTop:'1rem'}}>
            {/* Seiten: Top 5 + ausklappen */}
            <TopTable
              title="Top-Seiten (Pageviews)"
              rows={(!data.path && Array.isArray(data.top_pages)) ? data.top_pages : []}
              columns={[
                {
                  key: 'path',
                  label: 'Seite',
                  render: (row) => (
                    <a href="#"
                       onClick={(e)=>{ e.preventDefault(); setPath(row.path); setTimeout(load,0); }}>
                      {row.path}
                    </a>
                  )
                },
                { key: 'views', label: 'Views', align: 'right',
                  render: (row) => number(row.views) }
              ]}
              topN={5}
            />

            {/* QR-Codes: Top 5 + ausklappen */}
            <TopTable
              title="Top QR-Codes"
              rows={Array.isArray(data.qr_by_code) ? data.qr_by_code : []}
              columns={[
                {
                  key: 'code',
                  label: 'Code',
                  render: (row) => (
                    <a href="#"
                       onClick={(e)=>{ e.preventDefault(); setCode(row.code); setTimeout(load,0); }}>
                      {row.code}
                    </a>
                  )
                },
                { key: 'scans', label: 'Scans', align: 'right',
                  render: (row) => number(row.scans) }
              ]}
              topN={5}
            />
          </div>
          <div className="grid" style={{display:'grid', gap:'1rem', gridTemplateColumns:'1fr', marginTop:'1rem'}}>
  <TopTable
    title="Referrer (Pageviews)"
    rows={Array.isArray(data?.referrers) ? data.referrers : []}
    columns={[
      { key: 'referrer', label: 'Referrer' },
      { key: 'views',    label: 'Views', align: 'right', render: (row) => number(row.views) }
    ]}
    topN={5}
  />
</div>

          <GeoAnalytics path={path} />

          <div className="card" style={{marginTop:'1rem', background:'#f8fafc'}}>
            <strong>Hinweise:</strong>
            <ul style={{margin:'0.5rem 0 0 1.2rem'}}>
              <li><em>Alle Seiten</em> anzeigen: Filter leer lassen.</li>
              <li>Nach <em>Seite</em> filtern: Pfad (z. B. <code>/wohnen.html</code>) eingeben.</li>
              <li>Nach <em>QR-Slug</em> filtern: Code (z. B. <code>wohnen</code>) eingeben.</li>
              <li>CSV-Export erstellt eine Datei mit KPIs, PV-Zeitreihe und Top-Listen.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
